(() => {
  'use strict';

  let instanceCount = 0;

  class LatrandNavigation extends HTMLElement {
    connectedCallback() {
      this.initialize();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.clearTimers();
      cancelAnimationFrame(this.layoutFrame);
      cancelAnimationFrame(this.trapFrame);
      this.close({ restoreFocus: false });
    }

    initialize() {
      this.controller?.abort();
      this.clearTimers();
      cancelAnimationFrame(this.layoutFrame);
      this.controller = new AbortController();
      this.instanceId ||= ++instanceCount;
      this.isDesktop = this.dataset.device === 'desktop';
      this.viewport = window.matchMedia('(min-width: 1024px)');
      this.triggers = [...this.querySelectorAll('[data-lm-open]')];
      this.panels = [...this.querySelectorAll('[data-lm-panel]')];
      this.bar = this.querySelector('.lm-bar');
      this.activeKey = null;
      this.returnTarget = null;
      this.panelState = new Map();

      this.panels.forEach((panel, index) => {
        panel.id ||= `lm-panel-${this.instanceId}-${index}`;
        const state = {
          title: panel.querySelector('[data-lm-panel-title]')?.textContent,
          welcome: panel.querySelector('.lm-welcome')?.cloneNode(true),
          brandButtons: new Map([...panel.querySelectorAll('[data-lm-brand-collection]')].map((button) => [button.dataset.lmBrandCollection, button])),
          selection: null,
        };
        this.panelState.set(panel, state);
        this.resetPanel(panel);
        panel.hidden = true;
      });

      this.triggers.forEach((trigger) => {
        const panel = this.panelFor(trigger.dataset.lmOpen);
        trigger.setAttribute('aria-expanded', 'false');
        trigger.closest('[data-lm-direction]')?.classList.remove('is-active');
        if (panel) trigger.setAttribute('aria-controls', panel.id);
      });
      if (this.bar) this.bar.hidden = false;
      this.classList.remove('is-open');

      const options = { signal: this.controller.signal };
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.addEventListener('input', (event) => this.onInput(event), options);
      this.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.addEventListener('pointerover', (event) => this.onPointerOver(event), options);
      this.addEventListener('pointerout', (event) => this.onPointerOut(event), options);
      this.addEventListener('toggle', () => {
        this.refreshMobileTrap();
        this.scheduleLayout();
      }, { ...options, capture: true });

      document.addEventListener('pointerdown', (event) => {
        if (this.isDesktop && this.activeKey && !this.contains(event.target)) {
          this.close({ restoreFocus: false });
        }
      }, options);
      document.addEventListener('focusin', (event) => {
        if (this.isDesktop && this.activeKey && !this.contains(event.target)) {
          this.close({ restoreFocus: false });
        }
      }, options);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !event.defaultPrevented && this.isDesktop && this.activeKey) {
          event.preventDefault();
          this.close({ restoreFocus: true });
        }
      }, options);
      document.addEventListener('shopify:block:select', (event) => {
        const blockId = event.detail?.blockId;
        if (this.isDesktop === this.viewport.matches && this.panelFor(blockId)) {
          this.openPanel(blockId);
        }
      }, options);
      document.addEventListener('shopify:section:load', (event) => {
        if (event.target === this || event.target?.contains?.(this)) this.initialize();
      }, options);

      const updateLayout = () => this.scheduleLayout();
      this.addEventListener('load', updateLayout, { ...options, capture: true });
      window.addEventListener('resize', updateLayout, { ...options, passive: true });
      window.addEventListener('scroll', updateLayout, { ...options, capture: true, passive: true });
      this.viewport.addEventListener('change', () => this.close({ restoreFocus: false }), options);
      this.resizeObserver?.disconnect();
      this.resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(updateLayout) : null;
      if (this.bar) this.resizeObserver?.observe(this.bar);
      if (this.isDesktop) {
        this.querySelectorAll('.lm-panel-layout, .lm-brand-list, .lm-brand-host').forEach((item) => this.resizeObserver?.observe(item));
      }
    }

    panelFor(key) {
      return this.panels?.find((panel) => panel.dataset.lmPanel === key);
    }

    triggerFor(key) {
      return this.triggers?.find((trigger) => trigger.dataset.lmOpen === key);
    }

    openPanel(key, { focus = false } = {}) {
      const panel = this.panelFor(key);
      if (!panel || this.isDesktop !== this.viewport.matches) return;
      this.clearTimers();
      if (this.activeKey === key) {
        if (focus) this.focusPanel(panel);
        return;
      }

      document.querySelectorAll('latrand-navigation').forEach((navigation) => {
        if (navigation !== this && typeof navigation.close === 'function') {
          navigation.close({ restoreFocus: false });
        }
      });
      this.close({ restoreFocus: false });
      this.activeKey = key;
      this.returnTarget = this.triggerFor(key);
      this.panels.forEach((item) => { item.hidden = item !== panel; });
      this.triggers.forEach((trigger) => {
        const expanded = trigger.dataset.lmOpen === key;
        trigger.setAttribute('aria-expanded', String(expanded));
        trigger.closest('[data-lm-direction]')?.classList.toggle('is-active', expanded);
      });
      if (!this.isDesktop && this.bar) this.bar.hidden = true;
      const back = panel.querySelector('[data-lm-back]');
      if (!this.isDesktop && back) back.hidden = false;
      this.classList.add('is-open');
      this.updateLayout();
      this.resetScroll(panel);
      this.scheduleLayout();
      if (focus || !this.isDesktop) this.focusPanel(panel);
      this.refreshMobileTrap();
    }

    close({ restoreFocus = false } = {}) {
      const target = this.returnTarget || this.triggerFor(this.activeKey);
      this.clearTimers();
      this.activeKey = null;
      this.panels?.forEach((panel) => {
        panel.hidden = true;
        this.resetPanel(panel);
      });
      this.triggers?.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.closest('[data-lm-direction]')?.classList.remove('is-active');
      });
      if (this.bar) this.bar.hidden = false;
      this.classList.remove('is-open');
      this.returnTarget = null;
      if (restoreFocus && target?.isConnected) target.focus({ preventScroll: true });
      this.refreshMobileTrap();
    }

    resetPanel(panel) {
      const state = this.panelState?.get(panel);
      if (!state) return;
      state.selection = null;
      panel.classList.remove('is-detail');
      const scrollHint = panel.querySelector('[data-lm-scroll-hint]');
      if (scrollHint) {
        scrollHint.hidden = true;
        scrollHint.classList.remove('is-at-end');
        scrollHint.setAttribute('aria-hidden', 'true');
      }
      const title = panel.querySelector('[data-lm-panel-title]');
      if (title && state.title !== undefined) title.textContent = state.title;
      const back = panel.querySelector('[data-lm-back]');
      if (back) back.hidden = true;
      const roots = panel.querySelector('.lm-category-roots');
      if (roots) roots.hidden = false;
      const detailHost = panel.querySelector('.lm-detail-host');
      if (detailHost) {
        detailHost.replaceChildren();
        detailHost.hidden = true;
      }
      const browser = panel.querySelector('.lm-brand-browser');
      browser?.classList.remove('is-selected');
      const aside = panel.querySelector('.lm-brand-aside');
      if (aside) aside.hidden = false;
      const brandHost = panel.querySelector('.lm-brand-host');
      if (brandHost) {
        brandHost.replaceChildren(...(state.welcome ? [state.welcome.cloneNode(true)] : []));
        brandHost.hidden = !this.isDesktop;
      }
      panel.querySelectorAll('[data-lm-brand], [data-lm-category]').forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        if (button.hasAttribute('aria-expanded')) button.setAttribute('aria-expanded', 'false');
        button.hidden = false;
      });
      panel.querySelectorAll('[data-lm-search]').forEach((input) => { input.value = ''; });
      panel.querySelectorAll('[data-lm-featured]').forEach((group) => { group.hidden = false; });
      panel.querySelectorAll('.lm-empty').forEach((empty) => { empty.hidden = true; });
      panel.querySelectorAll('details[open]').forEach((detail) => { detail.open = false; });
    }

    selectDetail(button, kind, { focus = this.keyboardActivation, shortcut = null } = {}) {
      const panel = button.closest('[data-lm-panel]');
      const state = this.panelState.get(panel);
      const key = kind === 'brand' ? button.dataset.lmBrand : button.dataset.lmCategory;
      const templateAttribute = kind === 'brand' ? 'lmBrandContent' : 'lmCategoryContent';
      const template = [...panel.querySelectorAll('template')].find((item) => item.dataset[templateAttribute] === key);
      const host = panel.querySelector(kind === 'brand' ? '.lm-brand-host' : '.lm-detail-host');
      if (!state || !template || !host) return;
      if (state.selection?.button === button) return;
      this.clearTimers();

      state.selection = { button, kind, shortcut };
      host.replaceChildren(template.content.cloneNode(true));
      host.hidden = false;
      panel.querySelectorAll(kind === 'brand' ? '[data-lm-brand]' : '[data-lm-category]').forEach((item) => {
        item.setAttribute('aria-pressed', String(item === button));
        if (item.hasAttribute('aria-expanded')) item.setAttribute('aria-expanded', String(item === button));
      });
      if (!this.isDesktop) {
        panel.classList.add('is-detail');
        const title = panel.querySelector('[data-lm-panel-title]');
        if (title) title.textContent = button.dataset.lmTitle || button.textContent.trim();
        const back = panel.querySelector('[data-lm-back]');
        if (back) back.hidden = false;
        const source = panel.querySelector(kind === 'brand' ? '.lm-brand-aside' : '.lm-category-roots');
        if (source) source.hidden = true;
      }
      if (kind === 'brand') panel.querySelector('.lm-brand-browser')?.classList.add('is-selected');
      this.resetScroll(panel);
      // Preserve pointer users' place in the brand list; keyboard users enter the detail.
      if (!this.isDesktop || focus) {
        const heading = this.isDesktop ? host.querySelector('[data-lm-detail-title]') : panel.querySelector('[data-lm-panel-title]');
        (heading || host.querySelector('.lm-detail') || host).focus({ preventScroll: true });
      }
      this.refreshMobileTrap();
      this.scheduleLayout();
    }

    back() {
      const panel = this.panelFor(this.activeKey);
      const state = this.panelState.get(panel);
      if (!panel || !state?.selection) return;
      const { button, kind, shortcut } = state.selection;
      state.selection = null;
      panel.classList.remove('is-detail');
      const title = panel.querySelector('[data-lm-panel-title]');
      if (title && state.title !== undefined) title.textContent = state.title;
      const back = panel.querySelector('[data-lm-back]');
      if (back) back.hidden = this.isDesktop;
      const source = panel.querySelector(kind === 'brand' ? '.lm-brand-aside' : '.lm-category-roots');
      if (source) source.hidden = false;
      const host = panel.querySelector(kind === 'brand' ? '.lm-brand-host' : '.lm-detail-host');
      if (host) {
        host.replaceChildren(...(kind === 'brand' && state.welcome ? [state.welcome.cloneNode(true)] : []));
        host.hidden = kind !== 'brand' || !this.isDesktop;
      }
      panel.querySelector('.lm-brand-browser')?.classList.remove('is-selected');
      button.setAttribute('aria-pressed', 'false');
      if (button.hasAttribute('aria-expanded')) button.setAttribute('aria-expanded', 'false');
      this.resetScroll(panel);
      const shortcutTarget = shortcut && [...panel.querySelectorAll('[data-lm-featured-brand]')].find((item) => item.dataset.lmFeaturedBrand === shortcut && !item.closest('[hidden]'));
      const focusTarget = shortcutTarget || button;
      focusTarget.focus({ preventScroll: true });
      focusTarget.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
      this.refreshMobileTrap();
      this.scheduleLayout();
    }

    onClick(event) {
      const featured = event.target.closest('[data-lm-featured-brand]');
      if (featured && this.contains(featured) && !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        const panel = featured.closest('[data-lm-panel]');
        const button = this.panelState.get(panel)?.brandButtons.get(featured.dataset.lmFeaturedBrand);
        if (button) {
          event.preventDefault();
          this.selectDetail(button, 'brand', { focus: true, shortcut: featured.dataset.lmFeaturedBrand });
        }
        return;
      }
      if (event.target.closest('details[data-lm-submenu] > summary')) {
        this.clearTimers();
        return;
      }
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      this.clearTimers();
      this.keyboardActivation = event.detail === 0;
      if (button.hasAttribute('data-lm-open')) {
        if (this.activeKey === button.dataset.lmOpen) this.close({ restoreFocus: true });
        else this.openPanel(button.dataset.lmOpen, { focus: this.keyboardActivation });
      } else if (button.hasAttribute('data-lm-category')) {
        this.selectDetail(button, 'category');
      } else if (button.hasAttribute('data-lm-brand')) {
        this.selectDetail(button, 'brand');
      } else if (button.hasAttribute('data-lm-back')) {
        if (this.panelState.get(this.panelFor(this.activeKey))?.selection) this.back();
        else this.close({ restoreFocus: true });
      } else if (button.hasAttribute('data-lm-close')) {
        this.close({ restoreFocus: this.isDesktop });
        if (!this.isDesktop) this.closeMobileDrawer();
      }
      this.keyboardActivation = false;
    }

    onInput(event) {
      if (!event.target.matches('[data-lm-search]')) return;
      this.clearTimers();
      const panel = event.target.closest('[data-lm-panel]');
      const query = this.normalize(event.target.value);
      panel.querySelectorAll('[data-lm-featured]').forEach((group) => { group.hidden = query !== ''; });
      let visibleCount = 0;
      panel.querySelectorAll('[data-lm-brand]').forEach((button) => {
        const matches = this.normalize(button.dataset.lmTitle || button.textContent).includes(query);
        button.hidden = !matches;
        if (matches) visibleCount += 1;
      });
      const empty = panel.querySelector('.lm-empty');
      if (empty) empty.hidden = visibleCount > 0;
      this.refreshMobileTrap();
      this.scheduleLayout();
    }

    refreshMobileTrap() {
      if (this.isDesktop) return;
      cancelAnimationFrame(this.trapFrame);
      this.trapFrame = requestAnimationFrame(() => {
        if (!this.isConnected || !window.Alpine?.store('xMobileNav')?.show) return;
        const focused = document.activeElement;
        const focusStore = window.Alpine.store('xFocusElement');
        // The native drawer caches its endpoints. Refresh them after hiding a list,
        // then restore the current element so moving through the menu keeps its place.
        focusStore?.trapFocus('x-header-mobile', '');
        if (focused?.isConnected && focused.getClientRects().length) focused.focus({ preventScroll: true });
      });
    }

    closeMobileDrawer() {
      cancelAnimationFrame(this.trapFrame);
      const toggle = document.querySelector('button.header-mobile-toggle-icon[aria-controls="x-header-mobile"]');
      window.Alpine?.store('xMobileNav')?.close();
      window.Alpine?.store('xFocusElement')?.removeTrapFocus(toggle);
      toggle?.focus({ preventScroll: true });
    }

    normalize(value) {
      return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
    }

    onKeydown(event) {
      const submenu = event.target.closest('details[data-lm-submenu]');
      if (submenu && event.key === 'Escape' && submenu.open) {
        event.preventDefault();
        event.stopPropagation();
        submenu.open = false;
        submenu.querySelector('summary')?.focus({ preventScroll: true });
        return;
      }
      if (submenu && event.target.matches('summary') && event.key === 'ArrowDown') {
        event.preventDefault();
        submenu.open = true;
        submenu.querySelector('a')?.focus({ preventScroll: true });
        return;
      }
      if (event.key === 'Escape' && this.activeKey) {
        const state = this.panelState.get(this.panelFor(this.activeKey));
        if (!this.isDesktop && state?.selection) {
          event.preventDefault();
          event.stopPropagation();
          this.back();
        } else if (this.isDesktop) {
          event.preventDefault();
          event.stopPropagation();
          this.close({ restoreFocus: true });
        } else {
          // Let the native drawer process Escape after restoring its navigation.
          this.close({ restoreFocus: false });
        }
        return;
      }

      const direction = event.target.closest('[data-lm-direction]');
      const trigger = event.target.closest('[data-lm-open]') || direction?.querySelector('[data-lm-open]');
      if (trigger && this.isDesktop) {
        const index = this.triggers.indexOf(trigger);
        let next;
        const isRtl = getComputedStyle(this).direction === 'rtl';
        if (event.key === 'ArrowRight') next = index + (isRtl ? -1 : 1);
        if (event.key === 'ArrowLeft') next = index + (isRtl ? 1 : -1);
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.triggers.length - 1;
        if (next !== undefined) {
          event.preventDefault();
          const target = this.triggers[(next + this.triggers.length) % this.triggers.length];
          target.focus();
          if (this.activeKey) this.openPanel(target.dataset.lmOpen);
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.openPanel(trigger.dataset.lmOpen, { focus: true });
        }
      }

      const brand = event.target.closest('[data-lm-brand]');
      const search = event.target.matches('[data-lm-search]');
      if (!brand && !search) return;
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      if (search && event.key !== 'ArrowDown') return;
      const panel = event.target.closest('[data-lm-panel]');
      const buttons = [...panel.querySelectorAll('[data-lm-brand]')].filter((button) => !button.hidden);
      if (!buttons.length) return;
      event.preventDefault();
      const index = buttons.indexOf(brand);
      let next = event.key === 'ArrowUp' ? index - 1 : index + 1;
      if (event.key === 'Home' || search) next = 0;
      if (event.key === 'End') next = buttons.length - 1;
      buttons[(next + buttons.length) % buttons.length].focus({ preventScroll: true });
      buttons[(next + buttons.length) % buttons.length].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    }

    allowsHover(event) {
      return this.isDesktop && this.dataset.hover === 'true' && event.pointerType === 'mouse'
        && window.matchMedia('(any-hover: hover) and (any-pointer: fine)').matches;
    }

    onPointerOver(event) {
      clearTimeout(this.closeTimer);
      if (!this.allowsHover(event)) return;
      const brand = event.target.closest('[data-lm-brand]');
      if (brand && !brand.contains(event.relatedTarget)) {
        clearTimeout(this.brandTimer);
        this.brandTimer = setTimeout(() => {
          const panel = brand.closest('[data-lm-panel]');
          const host = panel?.querySelector('.lm-brand-host');
          if (!brand.isConnected || brand.hidden || panel !== this.panelFor(this.activeKey)
            || host?.contains(document.activeElement)) return;
          this.selectDetail(brand, 'brand', { focus: false });
        }, 140);
      }
      const summary = event.target.closest('details[data-lm-submenu] > summary');
      if (summary && !summary.contains(event.relatedTarget)) {
        clearTimeout(this.submenuTimer);
        this.submenuTimer = setTimeout(() => {
          const submenu = summary.parentElement;
          if (submenu.isConnected && submenu.closest('[data-lm-panel]') === this.panelFor(this.activeKey)) {
            submenu.open = true;
          }
        }, 120);
      }
      const direction = event.target.closest('[data-lm-direction]');
      const trigger = direction?.querySelector('[data-lm-open]') || event.target.closest('[data-lm-open]');
      if (!trigger || (direction || trigger).contains(event.relatedTarget)) return;
      clearTimeout(this.openTimer);
      this.openTimer = setTimeout(() => this.openPanel(trigger.dataset.lmOpen), 120);
    }

    onPointerOut(event) {
      if (!this.allowsHover(event)) return;
      const brand = event.target.closest('[data-lm-brand]');
      if (brand && !brand.contains(event.relatedTarget)) clearTimeout(this.brandTimer);
      const summary = event.target.closest('details[data-lm-submenu] > summary');
      if (summary && !summary.contains(event.relatedTarget)) clearTimeout(this.submenuTimer);
      const submenu = event.target.closest('details[data-lm-submenu]');
      if (submenu && !submenu.contains(event.relatedTarget) && !submenu.contains(document.activeElement)) {
        submenu.open = false;
      }
      const direction = event.target.closest('[data-lm-direction]');
      const trigger = direction?.querySelector('[data-lm-open]') || event.target.closest('[data-lm-open]');
      if (trigger && !(direction || trigger).contains(event.relatedTarget)) clearTimeout(this.openTimer);
      if (!this.contains(event.relatedTarget)) {
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => this.close({ restoreFocus: false }), 200);
      }
    }

    clearTimers() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      clearTimeout(this.brandTimer);
      clearTimeout(this.submenuTimer);
    }

    focusPanel(panel) {
      panel.querySelector('[data-lm-panel-title]')?.focus({ preventScroll: true });
    }

    resetScroll(panel) {
      panel.scrollTop = 0;
      panel.querySelectorAll('.lm-panel-layout, .lm-brand-host').forEach((item) => { item.scrollTop = 0; });
      if (!this.isDesktop) {
        const drawerScroll = document.getElementById('menu-navigation');
        if (drawerScroll) drawerScroll.scrollTop = 0;
      }
    }

    scheduleLayout() {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => this.updateLayout());
    }

    updateLayout() {
      if (!this.isDesktop || !this.activeKey || !this.bar) return;
      const rect = this.bar.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      if (rect.bottom <= 0 || rect.top >= viewportHeight) {
        this.close({ restoreFocus: false });
        return;
      }
      const top = Math.max(0, Math.round(rect.bottom));
      this.style.setProperty('--lm-top', `${top}px`);
      this.style.setProperty('--lm-available-height', `${Math.max(120, viewportHeight - top - 16)}px`);
      this.updateScrollCue(this.panelFor(this.activeKey));
    }

    updateScrollCue(panel) {
      const hint = panel?.querySelector('[data-lm-scroll-hint]');
      if (!hint) return;
      const scrollports = [...panel.querySelectorAll('.lm-panel-layout, .lm-brand-list, .lm-brand-host')]
        .filter((item) => item.clientHeight > 0 && item.getClientRects().length);
      // Exclude the footer's height from outer overflow detection so it cannot create its own cue.
      const reservedHeight = hint.hidden ? 0 : hint.getBoundingClientRect().height;
      const hasOverflow = scrollports.some((item) => {
        const availableHeight = item.clientHeight + (item.matches('.lm-panel-layout') ? reservedHeight : 0);
        return item.scrollHeight > availableHeight + 2;
      });
      hint.hidden = !hasOverflow;
      // Once overflow exists, keep the footer's height stable at the end of a list.
      // Re-read the scrollports after revealing it so the remaining distance is accurate.
      const hasMore = hasOverflow && scrollports.some((item) => item.scrollHeight - item.clientHeight - item.scrollTop > 4);
      hint.classList.toggle('is-at-end', !hasMore);
      hint.setAttribute('aria-hidden', String(!hasMore));
    }
  }

  if (!customElements.get('latrand-navigation')) customElements.define('latrand-navigation', LatrandNavigation);

  class LatrandBrandDirectory extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.cards = [...this.querySelectorAll('[data-brand-card]')];
      this.buttons = [...this.querySelectorAll('[data-brand-index]')];
      this.search = this.querySelector('[data-brand-search]');
      this.count = this.querySelector('[data-brand-count]');
      this.empty = this.querySelector('[data-brand-empty]');
      this.activeLetter = 'all';
      const letters = new Set(this.cards.map((card) => this.letterFor(card)));
      this.buttons.forEach((button) => {
        const available = button.dataset.brandIndex === 'all' || letters.has(button.dataset.brandIndex);
        button.disabled = !available;
        if (button.hasAttribute('data-brand-cyrillic')) button.hidden = !available;
      });
      this.querySelector('[data-brand-tools]')?.removeAttribute('hidden');
      const options = { signal: this.controller.signal };
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-brand-index]');
        if (!button || button.disabled || !this.contains(button)) return;
        this.activeLetter = button.dataset.brandIndex;
        this.update();
      }, options);
      this.search?.addEventListener('input', () => this.update(), options);
      this.update();
    }

    disconnectedCallback() { this.controller?.abort(); }

    letterFor(card) {
      const letter = card.dataset.brandLetter?.toLocaleUpperCase('bg') || '';
      return /^[0-9]$/.test(letter) ? '0-9' : letter;
    }

    normalize(value) {
      return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('bg').trim();
    }

    update() {
      const query = this.normalize(this.search?.value);
      let visible = 0;
      this.cards.forEach((card) => {
        const matches = (this.activeLetter === 'all' || this.letterFor(card) === this.activeLetter)
          && this.normalize(card.dataset.brandTitle).includes(query);
        card.hidden = !matches;
        if (matches) visible += 1;
      });
      this.buttons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.brandIndex === this.activeLetter)));
      if (this.count) this.count.textContent = String(visible);
      if (this.empty) this.empty.hidden = visible !== 0;
    }
  }

  if (!customElements.get('latrand-brand-directory')) customElements.define('latrand-brand-directory', LatrandBrandDirectory);

})();
